import React from 'react';

// Lazy load all page components with appropriate fallbacks
const Dashboard = React.lazy(() => import('../pages/dashboard/Dashboard'));

const SignIn = React.lazy(() => import('../pages/auth/SignIn'));

// Settings Components
const Academic = React.lazy(() => import('../pages/settings/Academic'));
const HolidayManager = React.lazy(() => import('../pages/settings/HolidayManager'));

// Class & Sections Components
const DashboardOverview = React.lazy(() =>
  import('../pages/class-sections/DashboardOverview').then(module => ({
    default: module.DashboardOverview
  }))
);

const EnhancedClassSections = React.lazy(() =>
  import('../pages/class-sections/ClassSections').then(module => ({
    default: module.ClassSections
  }))
);

const SectionManagement = React.lazy(() =>
  import('../pages/class-sections/SectionManagement').then(module => ({
    default: module.SectionManagement
  }))
);

const SubjectManagement = React.lazy(() =>
  import('../pages/class-sections/SubjectManagement').then(module => ({
    default: module.SubjectManagement
  }))
);

// Student Components
const StudentDashboard = React.lazy(() => import('../pages/students/StudentDashboard'));
const StudentManagement = React.lazy(() => import('../pages/students/StudentManagement'));
const StudentGroupManagement = React.lazy(() => import('../pages/students/StudentGroupManagement'));
const StudentAttendance = React.lazy(() => import('../pages/students/StudentAttendance'));
const SiblingManagement = React.lazy(() => import('../pages/students/SiblingManagement'));
const StudentPromotion = React.lazy(() => import('../pages/students/StudentPromotion'));

// Fee Components
const FeeDashboard = React.lazy(() => import('../pages/fees/FeeDashboard'));
const FeeAnalytics = React.lazy(() => import('../pages/fees/FeeAnalytics'));
const FeeCategoryManagement = React.lazy(() => import('../pages/fees/FeeCategoryManagement'));
const FeeStructureManagement = React.lazy(() => import('../pages/fees/FeeStructureManagement'));
const StudentFeeManagement = React.lazy(() => import('../pages/fees/StudentFeeManagement'));
const FeePaymentManagement = React.lazy(() => import('../pages/fees/FeePaymentManagement'));

// Exam Management Components
const ExamManagement = React.lazy(() => import('../pages/exams/ExamManagement'));
const OnlineExamManagement = React.lazy(() =>
  import('../pages/exams').then(module => ({
    default: module.OnlineExamManagement
  }))
);

// Student Exam Components
const StudentExamRoutes = React.lazy(() => import('../pages/exams/StudentExamRoutes'));

// Assignment Management Components
const AssignmentManagement = React.lazy(() => import('../pages/assignments/AssignmentManagement'));

// Teacher Management Components
const Teachers = React.lazy(() => import('../pages/teachers/Teachers'));

// HR/Payroll Management Components  
const Employees = React.lazy(() => import('../pages/hr-payroll/Employees'));
const EmployeeAttendance = React.lazy(() => import('../pages/hr-payroll/EmployeeAttendance'));
const SalaryComponents = React.lazy(() => import('../pages/hr-payroll/SalaryComponents'));
const SalaryStructures = React.lazy(() => import('../pages/hr-payroll/SalaryStructures'));
const EmployeeSalary = React.lazy(() => import('../pages/hr-payroll/EmployeeSalary'));
const PayrollRuns = React.lazy(() => import('../pages/hr-payroll/PayrollRuns'));
const PayrollRunDetail = React.lazy(() => import('../pages/hr-payroll/PayrollRunDetail'));
const StatutoryConfig = React.lazy(() => import('../pages/hr-payroll/StatutoryConfig'));
const LeaveTypes = React.lazy(() => import('../pages/hr-payroll/LeaveTypes'));
const LeaveRequests = React.lazy(() => import('../pages/hr-payroll/LeaveRequests'));
const LeaveCalendar = React.lazy(() => import('../pages/hr-payroll/LeaveCalendar'));
const Loans = React.lazy(() => import('../pages/hr-payroll/Loans'));
const Reimbursements = React.lazy(() => import('../pages/hr-payroll/Reimbursements'));
const Documents = React.lazy(() => import('../pages/hr-payroll/Documents'));
const Form16 = React.lazy(() => import('../pages/hr-payroll/Form16'));
const TaxDeclarations = React.lazy(() => import('../pages/hr-payroll/TaxDeclarations'));

// Other Components
const Tracking = React.lazy(() => import('../pages/transport/Tracking'));
const RoleManagement = React.lazy(() => import('../pages/roles'));
const MenuManagement = React.lazy(() => import('../pages/menu-management'));

// Feedback Management Component
const FeedbackManagement = React.lazy(() => import('../pages/feedback-management'));
const FeedbackListPage = React.lazy(() => import('../pages/feedback-management/FeedbackListPage'));
const FeedbackStatsPage = React.lazy(() => import('../pages/feedback-management/FeedbackStatsPage'));

// Template Builder Component
const TemplateBuilder = React.lazy(() => import('../pages/template-builder/TemplateBuilder'));

// Calendar Component
const SchoolCalendar = React.lazy(() => import('../pages/calendar/ProtonCalendar'));

// Communication Components
const ChatInterface = React.lazy(() => import('../pages/chat'));

// Transfer Certificate Components
const TransferCertificateIssue = React.lazy(() => import('../pages/transfer-certificates/TransferCertificateIssue'));
const TransferCertificateList = React.lazy(() => import('../pages/transfer-certificates/TransferCertificateList'));
const TransferCertificatePreview = React.lazy(() => import('../pages/transfer-certificates/TransferCertificatePreview'));

// Store Management Components
const StoreDashboard = React.lazy(() => import('../pages/store-management/StoreDashboard'));
const StoreItems = React.lazy(() => import('../pages/store-management/StoreItems'));
const StoreCategories = React.lazy(() => import('../pages/store-management/StoreCategories'));
const StoreSuppliers = React.lazy(() => import('../pages/store-management/StoreSuppliers'));
const StoreRequisitions = React.lazy(() => import('../pages/store-management/StoreRequisitions'));
const StorePurchaseOrders = React.lazy(() => import('../pages/store-management/StorePurchaseOrders'));
const StoreGoodsReceipts = React.lazy(() => import('../pages/store-management/StoreGoodsReceipts'));
const StoreStockLedger = React.lazy(() => import('../pages/store-management/StoreStockLedger'));
const StoreAdjustments = React.lazy(() => import('../pages/store-management/StoreAdjustments'));
const StoreIssues = React.lazy(() => import('../pages/store-management/StoreIssues'));
const StoreAlerts = React.lazy(() => import('../pages/store-management/StoreAlerts'));
const StoreReports = React.lazy(() => import('../pages/store-management/StoreReports'));
const StoreTransfers = React.lazy(() => import('../pages/store-management/StoreTransfers'));
const StoreUnits = React.lazy(() => import('../pages/store-management/StoreUnits'));

// Library Management Components
const LibraryDashboard = React.lazy(() => import('../pages/library/LibraryDashboard'));
const LibraryCatalog = React.lazy(() => import('../pages/library/LibraryCatalog'));
const LibraryMembers = React.lazy(() => import('../pages/library/LibraryMembers'));
const LibraryCirculation = React.lazy(() => import('../pages/library/LibraryCirculation'));
const LibraryRenewals = React.lazy(() => import('../pages/library/LibraryRenewals'));
const LibraryReservations = React.lazy(() => import('../pages/library/LibraryReservations'));
const LibraryFines = React.lazy(() => import('../pages/library/LibraryFines'));
const LibraryReports = React.lazy(() => import('../pages/library/LibraryReports'));
const LibrarySettings = React.lazy(() => import('../pages/library/LibrarySettings'));

// Transport Management Components
const TransportDashboard = React.lazy(() => import('../pages/transport/TransportDashboard'));
const TransportVehicles = React.lazy(() => import('../pages/transport/TransportVehicles'));
const TransportRoutes = React.lazy(() => import('../pages/transport/TransportRoutes'));
const TransportStudents = React.lazy(() => import('../pages/transport/TransportStudents'));
const TransportAttendance = React.lazy(() => import('../pages/transport/TransportAttendance'));
const TransportMaintenance = React.lazy(() => import('../pages/transport/TransportMaintenance'));
const TransportReports = React.lazy(() => import('../pages/transport/TransportReports'));
const TransportSettings = React.lazy(() => import('../pages/transport/TransportSettings'));
const TransportDrivers = React.lazy(() => import('../pages/transport/TransportDrivers'));
const SubscriptionPage = React.lazy(() => import('../pages/subscription/SubscriptionPage'));


// Export all components
export {
  Dashboard,
  SignIn,
  Academic,
  HolidayManager,
  DashboardOverview,
  EnhancedClassSections,
  SectionManagement,
  SubjectManagement,
  StudentDashboard,
  StudentManagement,
  StudentGroupManagement,
  StudentAttendance,
  SiblingManagement,
  StudentPromotion,
  FeeDashboard,
  FeeAnalytics,
  FeeCategoryManagement,
  FeeStructureManagement,
  StudentFeeManagement,
  FeePaymentManagement,
  ExamManagement,
  OnlineExamManagement,
  StudentExamRoutes,
  AssignmentManagement,
  Teachers,
  Employees,
  EmployeeAttendance,
  SalaryComponents,
  SalaryStructures,
  EmployeeSalary,
  PayrollRuns,
  PayrollRunDetail,
  StatutoryConfig,
  LeaveTypes,
  LeaveRequests,
  LeaveCalendar,
  Loans,
  Reimbursements,
  Documents,
  Form16,
  TaxDeclarations,
  Tracking,
  RoleManagement,
  MenuManagement,
  FeedbackManagement,
  FeedbackListPage,
  FeedbackStatsPage,
  TemplateBuilder,
  SchoolCalendar,
  ChatInterface,
  StoreDashboard,
  StoreItems,
  StoreCategories,
  StoreSuppliers,
  StoreRequisitions,
  StorePurchaseOrders,
  StoreGoodsReceipts,
  StoreStockLedger,
  StoreAdjustments,
  StoreIssues,
  StoreAlerts,
  StoreReports,
  StoreTransfers,
  StoreUnits,
  LibraryDashboard,
  LibraryCatalog,
  LibraryMembers,
  LibraryCirculation,
  LibraryRenewals,
  LibraryReservations,
  LibraryFines,
  LibraryReports,
  LibrarySettings,
  TransportDashboard,
  TransportVehicles,
  TransportRoutes,
  TransportStudents,
  TransportAttendance,
  TransportMaintenance,
  TransportReports,
  TransportSettings,
  TransportDrivers,
  TransferCertificateIssue,
  TransferCertificateList,
  TransferCertificatePreview,
  SubscriptionPage,
};
